import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    FormControl,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import {
    useGetTicketType,
    addTicketType,
    editTicketType,
} from '@openapi/ticket-types/ticket-types';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { showsAutocomplete, ticketSeatsAutocomplete } from '../../autocomplete';
import AutocompleteSelect from '../../components/atom/AutoCompleteSelect';
import ImprovedPriceMatrixEditor from './components/ImprovedPriceMatrixEditor';
import { convertLinesToPrice, convertPriceToLines } from '../../helpers/priceMatrixHelpers';
import { useTranslation } from 'react-i18next';
import CustomFieldsEditor from './components/CustomFieldsEditor';
import {
    convertCustomFieldsToLines,
    convertLinesToCustomFields,
} from '../../helpers/customFieldsHelpers';
import LanguageIcon from '@mui/icons-material/Language';
import SvgViewer from '../../components/svgeditor/SvgViewer';
import mapFromDbToElement from '../seats/helpers/mapFromDbToElement';
import { useGetTicketSeats } from '@openapi/ticket-seats/ticket-seats';
import { useArrangementElements } from '../../hooks/useArrangementElements';

const toCheckinMinutes = (v) => Math.max(0, Number(v) || 0);

function TicketTypeForm({ values, setFieldValue, t }) {
    const searchParams = { showId: values.showId, isBookable: true };

    const { data: seatsData } = useGetTicketSeats(Number(values.showId), {
        pageSize: 10000,
    });

    const allElements = (seatsData?.ticketSeats || []).map(mapFromDbToElement);
    const { displayElements } = useArrangementElements(values.showId, allElements);
    const selectedSeats = values?.seats || [];
    const bookableIds = new Set(allElements.filter((e) => e.bookable).map((e) => e.id));
    const bookableCount = bookableIds.size;

    const colorMap = {};
    const isValidHex = /^#[0-9a-fA-F]{6}$/.test(values.color);
    const seatColor = isValidHex ? values.color : '#1f77b4';
    allElements.forEach((el) => {
        if (selectedSeats.includes(el.id)) {
            colorMap[el.id] = seatColor;
        } else if (!el.bookable) {
            colorMap[el.id] = '#cccccc';
        } else {
            colorMap[el.id] = 'white';
        }
    });

    const handleClickSeat = (id) => {
        if (!bookableIds.has(id)) return;
        if (selectedSeats.includes(id)) {
            setFieldValue(
                'seats',
                selectedSeats.filter((s) => s !== id),
            );
        } else {
            setFieldValue('seats', [...selectedSeats, id]);
        }
    };

    const handleBoxSelect = (ids) => {
        const selectable = ids.filter((id) => bookableIds.has(id));
        if (selectable.length === 0) return;
        const allSelected = selectable.every((id) => selectedSeats.includes(id));
        if (allSelected) {
            setFieldValue(
                'seats',
                selectedSeats.filter((s) => !selectable.includes(s)),
            );
        } else {
            const toAdd = selectable.filter((id) => !selectedSeats.includes(id));
            setFieldValue('seats', [...selectedSeats, ...toAdd]);
        }
    };

    const handleSetSeats = (ev) => setFieldValue('seats', ev);

    // Pre-populate autocomplete cache with all bookable seats
    const seatInitData = (seatsData?.ticketSeats || []).map((s) => ({
        id: s.id,
        name: s.name,
    }));

    return (
        <>
            <Grid2 size={{ xs: 12 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Chip
                        label={`${selectedSeats.length} / ${bookableCount}`}
                        color={selectedSeats.length > 0 ? 'primary' : 'default'}
                        variant="outlined"
                        size="small"
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => setFieldValue('seats', [])}
                        disabled={selectedSeats.length === 0}
                    >
                        {t('common.clearAll')}
                    </Button>
                </Box>
                <AutocompleteSelect
                    initCall={seatInitData}
                    autocompleteCall={ticketSeatsAutocomplete.search}
                    value={selectedSeats}
                    setValue={handleSetSeats}
                    multiple
                    searchParams={searchParams}
                >
                    <TextField label={t('tickettypes.columns.ticketSeats')} />
                </AutocompleteSelect>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Alert severity="info" sx={{ my: 2 }}>
                    {t('tickettypes.seatMapHintClick')}{' '}
                    <strong>{t('tickettypes.seatMapHintShift')}</strong>
                </Alert>
                {allElements.length > 0 && (
                    <SvgViewer
                        elements={displayElements}
                        colorMap={colorMap}
                        onClickElement={handleClickSeat}
                        onBoxSelect={handleBoxSelect}
                        inCartSeatIds={[]}
                    />
                )}
            </Grid2>
        </>
    );
}

function EditTicketTypePage({ newTicketType }) {
    const { t } = useTranslation();
    const { showId, ticketTypeId } = useParams();
    const { showModal } = useModals();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [ticketType, setTicketType] = useState(null);
    const { notifyError, notifySuccess } = useNotify();

    const {
        data: ticketTypeData,
        isLoading: ticketTypeLoading,
        error: _ticketTypeError,
    } = useGetTicketType(Number(ticketTypeId), {
        query: {
            enabled: !newTicketType && !!ticketTypeId,
            onError: (err) => {
                navigate(`/shows/${showId}/tickettypes`);
                notifyError(err);
            },
        },
    });

    const defaultTicketType = {
        name: t('tickettypes.defaultName'),
        nameI18n: {},
        description: t('tickettypes.defaultDescription'),
        descriptionI18n: {},
        showId,
        showInList: true,
        canBuyAlone: true,
        priceMatrix: [],
        guestVisible: true,
        automaticSeating: 'manual',
        customFields: [],
        blockLimit: null,
        checkinTimeBeforeStart: 0,
        color: '#eeeeee',
        seats: [],
        venueId: null, // languages are not available for new ticket types, only edit
    };

    useEffect(() => {
        if (!newTicketType) {
            if (ticketTypeData) {
                setTicketType({
                    id: ticketTypeData.id,
                    name: ticketTypeData.name,
                    nameI18n: ticketTypeData.nameI18n,
                    description: ticketTypeData.description,
                    descriptionI18n: ticketTypeData.descriptionI18n,
                    showId: ticketTypeData.showId,
                    showTitle: ticketTypeData.showTitle,
                    venueId: ticketTypeData.venueId,
                    showInList: ticketTypeData.showInList,
                    canBuyAlone: ticketTypeData.canBuyAlone,
                    priceMatrix: convertPriceToLines(ticketTypeData.priceMatrix),
                    guestVisible: ticketTypeData.guestVisible,
                    automaticSeating: ticketTypeData.automaticSeating,
                    customFields: convertCustomFieldsToLines(ticketTypeData.customFields),
                    blockLimit: ticketTypeData.blockLimit,
                    checkinTimeBeforeStart: ticketTypeData.checkinTimeBeforeStart || 0,
                    color: ticketTypeData.color,
                    seats: ticketTypeData.seats,
                });
            }
            setLoading(ticketTypeLoading);
        } else {
            setLoading(false);
        }
    }, [showId, ticketTypeId, ticketTypeData, newTicketType, ticketTypeLoading]);

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editTicketType(Number(ticketTypeId), {
                        name: values.name,
                        nameI18n: values.nameI18n,
                        description: values.description,
                        descriptionI18n: values.descriptionI18n,
                        showId: parseInt(values.showId, 10),
                        showInList: values.showInList,
                        canBuyAlone: values.canBuyAlone,
                        priceMatrix: convertLinesToPrice(values.priceMatrix),
                        guestVisible: values.guestVisible,
                        automaticSeating: values.automaticSeating,
                        customFields: convertLinesToCustomFields(values.customFields),
                        blockLimit: values.blockLimit || null,
                        checkinTimeBeforeStart: toCheckinMinutes(values.checkinTimeBeforeStart),
                        color: values.color,
                        seats: values.seats,
                    });
                    navigate(`/shows/${showId}/tickettypes`);
                    notifySuccess(t('tickettypes.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newTicketType) {
            return onEdit(values);
        }
        try {
            await addTicketType({
                name: values.name,
                nameI18n: values.nameI18n,
                description: values.description,
                descriptionI18n: values.descriptionI18n,
                showId: parseInt(values.showId, 10),
                showInList: values.showInList,
                canBuyAlone: values.canBuyAlone,
                priceMatrix: convertLinesToPrice(values.priceMatrix),
                guestVisible: values.guestVisible,
                automaticSeating: values.automaticSeating,
                customFields: convertLinesToCustomFields(values.customFields),
                blockLimit: values.blockLimit || null,
                checkinTimeBeforeStart: toCheckinMinutes(values.checkinTimeBeforeStart),
                color: values.color,
                seats: values.seats,
            });
            navigate(`/shows/${showId}/tickettypes`);
            notifySuccess(t('tickettypes.notifications.added'));
        } catch (error) {
            notifyError(error);
        }
    };

    if (loading) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        showId: Yup.number().required(t('common.required')),
        showInList: Yup.boolean().required(t('common.required')),
        canBuyAlone: Yup.boolean().required(t('common.required')),
        priceMatrix: Yup.array().required(t('common.required')),
        guestVisible: Yup.boolean().required(t('common.required')),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={ticketType || { ...defaultTicketType }}
            validationSchema={validateSchema}
            // to avoid re-rendering the AutocompleteSelect
            validateOnChange={false}
            validateOnBlur={true}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => {
                return (
                    <Form>
                        <PageWithTitle
                            title={
                                newTicketType
                                    ? t('tickettypes.newTicketTypeName')
                                    : `${ticketType?.name ?? ''}`
                            }
                        >
                            <Grid2 size={{ xs: 12, md: 10 }}>
                                <BlockWithTitle title={t('tickettypes.title')}>
                                    <Grid2 size={{ xs: 10, sm: 11 }}>
                                        <TextField
                                            fullWidth
                                            label={t('tickettypes.columns.name')}
                                            value={values.name}
                                            onChange={(ev) =>
                                                setFieldValue('name', ev.target.value)
                                            }
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name ? errors.name : ''}
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 2, sm: 1 }}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Tooltip title={t('common.translations')}>
                                            <IconButton
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.TRANSLATIONS, {
                                                        translations: values.nameI18n,
                                                        setTranslations: (translations) =>
                                                            setFieldValue('nameI18n', translations),
                                                        item: 'tickettypes',
                                                        column: 'name',
                                                        venueId: values.venueId,
                                                        originalString: values.name,
                                                    });
                                                }}
                                                disabled={!values.venueId}
                                                size="small"
                                                edge="end"
                                            >
                                                <LanguageIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <AutocompleteSelect
                                            initCall={showsAutocomplete.init}
                                            autocompleteCall={showsAutocomplete.search}
                                            value={values?.showId}
                                            setValue={(ev) => setFieldValue('showId', ev)}
                                            disabled={true}
                                        >
                                            <TextField label={t('shows.model')} required />
                                        </AutocompleteSelect>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label={t('tickettypes.columns.color')}
                                            value={values.color || ''}
                                            onChange={(ev) => {
                                                setFieldValue('color', ev.target.value);
                                            }}
                                            helperText={t('tickettypes.colorInfo')}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 10, sm: 11 }}>
                                        <TextField
                                            fullWidth
                                            label={t('tickettypes.columns.description')}
                                            value={values.description}
                                            onChange={(ev) =>
                                                setFieldValue('description', ev.target.value)
                                            }
                                            error={
                                                touched.description && Boolean(errors.description)
                                            }
                                            helperText={
                                                touched.description ? errors.description : ''
                                            }
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 2, sm: 1 }}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Tooltip title={t('common.translations')}>
                                            <IconButton
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.TRANSLATIONS, {
                                                        translations: values.descriptionI18n,
                                                        setTranslations: (translations) =>
                                                            setFieldValue(
                                                                'descriptionI18n',
                                                                translations,
                                                            ),
                                                        item: 'tickettypes',
                                                        column: 'description',
                                                        venueId: values.venueId,
                                                        originalString: values.description,
                                                    });
                                                }}
                                                disabled={!values.venueId}
                                                size="small"
                                                edge="end"
                                            >
                                                <LanguageIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 12, sm: 6, md: 3 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!values.showInList}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'showInList',
                                                            !!e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={t('tickettypes.columns.showInList')}
                                            labelPlacement="end"
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 12, sm: 6, md: 3 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!values.canBuyAlone}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'canBuyAlone',
                                                            !!e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={t('tickettypes.columns.canBuyAlone')}
                                            labelPlacement="end"
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 12, sm: 6, md: 3 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!values.guestVisible}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'guestVisible',
                                                            !!e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={t('tickettypes.columns.guestVisible')}
                                            labelPlacement="end"
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 12, sm: 6, md: 3 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel>
                                                {t('tickettypes.columns.automaticSeating')}
                                            </InputLabel>
                                            <Select
                                                value={values.automaticSeating}
                                                label={t('tickettypes.columns.automaticSeating')}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'automaticSeating',
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                <MenuItem value="manual">
                                                    {t('tickettypes.automaticSeating.manual')}
                                                </MenuItem>
                                                <MenuItem value="auto">
                                                    {t('tickettypes.automaticSeating.auto')}
                                                </MenuItem>
                                                <MenuItem value="fullauto">
                                                    {t('tickettypes.automaticSeating.fullauto')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label={t('tickettypes.columns.blockLimit')}
                                            type="number"
                                            value={values.blockLimit || ''}
                                            onChange={(ev) => {
                                                const value = ev.target.value;
                                                setFieldValue(
                                                    'blockLimit',
                                                    value === '' ? null : parseInt(value, 10),
                                                );
                                            }}
                                            helperText={t('tickettypes.blockLimitInfo')}
                                            slotProps={{ htmlInput: { min: 0 } }}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label={t('tickettypes.columns.checkinTimeBeforeStart')}
                                            type="number"
                                            value={values.checkinTimeBeforeStart ?? ''}
                                            onChange={(ev) => {
                                                const v = ev.target.value;
                                                setFieldValue(
                                                    'checkinTimeBeforeStart',
                                                    v === '' ? '' : toCheckinMinutes(v),
                                                );
                                            }}
                                            helperText={t('tickettypes.checkinTimeBeforeStartInfo')}
                                            slotProps={{ htmlInput: { min: 0 } }}
                                        />
                                    </Grid2>
                                </BlockWithTitle>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 10 }}>
                                <BlockWithTitle title={t('tickettypes.columns.ticketSeats')}>
                                    <TicketTypeForm
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        t={t}
                                    />
                                </BlockWithTitle>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 10 }}>
                                <BlockWithTitle title={t('tickettypes.columns.priceMatrix')}>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Alert
                                            severity="warning"
                                            sx={{ '& .MuiAlert-message': { textAlign: 'left' } }}
                                        >
                                            {t('tickettypes.priceMatrixWarning')}
                                            <Link to={`/shows/${showId}/ticketoptions`}>
                                                {t('ticketoptions.title')}
                                            </Link>
                                        </Alert>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <ImprovedPriceMatrixEditor
                                            values={values}
                                            setFieldValue={setFieldValue}
                                        />
                                    </Grid2>
                                </BlockWithTitle>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 10 }}>
                                <BlockWithTitle title={t('tickettypes.columns.customFields')}>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Alert
                                            severity="info"
                                            sx={{ '& .MuiAlert-message': { textAlign: 'left' } }}
                                        >
                                            {t('tickettypes.customFieldsInfo')}
                                        </Alert>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <CustomFieldsEditor
                                            values={values}
                                            setFieldValue={setFieldValue}
                                        />
                                    </Grid2>
                                </BlockWithTitle>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 10 }}>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 6, md: 2 }}>
                                        <CommonButton
                                            fullWidth
                                            onClick={() => navigate(`/shows/${showId}/tickettypes`)}
                                            label={t('common.cancel')}
                                            buttonType="secondary"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 6, md: 2 }}>
                                        <CommonButton
                                            fullWidth
                                            disabled={isSubmitting}
                                            type="submit"
                                            label={t('common.save')}
                                            buttonType="primary"
                                        />
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </PageWithTitle>
                    </Form>
                );
            }}
        </Formik>
    );
}

export default EditTicketTypePage;
